/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail, Copy, Trash2, Star, StarOff, Paperclip, X as XIcon } from "lucide-react";

import { api } from "@/lib/api";
import { formatApiError } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

/**
 * EmailComposer — shared subject/HTML editor with live preview toggle,
 * template picker, clone-with-rename, attachments, and send.
 *
 * Props:
 *  - open: boolean
 *  - onClose(): void
 *  - onSent(result): void — result comes from the caller's onSend handler
 *  - title: string ("Send email")
 *  - kind: string (e.g. "job_annual_ready" | "job_gst_ready" | "welcome") —
 *      restricts the template picker to matching templates. Pass null to
 *      show all templates.
 *  - loadPreview({ templateId }): Promise<{ to, to_name, subject, html,
 *      template? }> — caller supplies. Called on mount + when the picker
 *      changes.
 *  - onSend({ subject, html, attachments, template_id }): Promise<any> —
 *      caller wires the send request.
 *  - toEditable: bool (default false) — allow editing the "To" field.
 *  - extraFooter?: React node — rendered above the Cancel/Send buttons.
 *  - showTemplatePicker?: bool (default true).
 */
export default function EmailComposer({
    open,
    onClose,
    onSent,
    title = "Send email",
    kind = null,
    loadPreview,
    onSend,
    toEditable = false,
    extraFooter = null,
    showTemplatePicker = true,
}) {
    const [templates, setTemplates] = useState([]);
    const [templateId, setTemplateId] = useState("");
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [form, setForm] = useState({ to: "", to_name: "", subject: "", html: "" });
    const [attachments, setAttachments] = useState([]);
    const [showPreview, setShowPreview] = useState(true);
    const [cloneOpen, setCloneOpen] = useState(false);
    const fileRef = useRef();

    // Reset when reopened
    useEffect(() => {
        if (!open) {
            setTemplates([]); setTemplateId(""); setForm({ to: "", to_name: "", subject: "", html: "" });
            setAttachments([]); setShowPreview(true);
        }
    }, [open]);

    // Load available templates once per open
    useEffect(() => {
        if (!open || !showTemplatePicker) return;
        (async () => {
            try {
                const params = kind ? `?kind=${encodeURIComponent(kind)}` : "";
                const { data } = await api.get(`/email-templates/${params}`);
                setTemplates(data.rows || []);
            } catch (e) {
                toast.error(formatApiError(e));
            }
        })();
    }, [open, kind, showTemplatePicker]);

    // Load initial preview (server picks the default)
    useEffect(() => {
        if (!open) return;
        (async () => {
            setLoading(true);
            try {
                const res = await loadPreview({ templateId: null });
                setForm({
                    to: res.to || "", to_name: res.to_name || "",
                    subject: res.subject || "", html: res.html || "",
                });
                if (res.template?.id) setTemplateId(res.template.id);
            } catch (e) {
                toast.error(formatApiError(e));
                onClose?.();
            } finally {
                setLoading(false);
            }
        })();
    }, [open]);

    async function switchTemplate(nextId) {
        setTemplateId(nextId);
        setLoading(true);
        try {
            const res = await loadPreview({ templateId: nextId });
            setForm((f) => ({
                ...f,
                subject: res.subject || "",
                html: res.html || "",
            }));
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }

    async function handleAttach(files) {
        if (!files || !files.length) return;
        const arr = Array.from(files);
        const encoded = [];
        for (const f of arr) {
            if (f.size > 10 * 1024 * 1024) {
                toast.error(`${f.name} exceeds the 10 MB per-file limit.`);
                continue;
            }
            const buf = await f.arrayBuffer();
            const b64 = btoa(String.fromCharCode(...new Uint8Array(buf)));
            encoded.push({
                name: f.name,
                content_type: f.type || "application/octet-stream",
                content_base64: b64,
                size: f.size,
            });
        }
        setAttachments((a) => [...a, ...encoded]);
        if (fileRef.current) fileRef.current.value = "";
    }

    function removeAttachment(idx) {
        setAttachments((a) => a.filter((_, i) => i !== idx));
    }

    async function saveEditsToTemplate() {
        // Save the current subject/html back onto the selected (non-system) template
        if (!templateId) return;
        const t = templates.find((x) => x.id === templateId);
        if (!t || t.is_system) {
            toast.error("Clone this system template first, then edit the copy.");
            return;
        }
        try {
            const { data } = await api.patch(`/email-templates/${templateId}`, {
                subject_template: form.subject,
                html_template: form.html,
            });
            setTemplates((rows) => rows.map((r) => (r.id === data.id ? data : r)));
            toast.success("Template updated");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    async function markDefault() {
        if (!templateId) return;
        const t = templates.find((x) => x.id === templateId);
        if (!t || t.is_system) {
            toast.error("Only your own templates can be marked default. Clone this one first.");
            return;
        }
        try {
            const { data } = await api.patch(`/email-templates/${templateId}`, { is_default: true });
            setTemplates((rows) => rows.map((r) => (
                r.kind === data.kind ? { ...r, is_default: r.id === data.id } : r
            )));
            toast.success("Set as default for this kind");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    async function send() {
        setSending(true);
        try {
            const res = await onSend({
                to: form.to,
                subject: form.subject,
                html: form.html,
                attachments: attachments.map((a) => ({
                    name: a.name, content_type: a.content_type, content_base64: a.content_base64,
                })),
                template_id: templateId || null,
            });
            toast.success(`Email sent to ${form.to}`);
            onSent?.(res);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSending(false);
        }
    }

    const currentTmpl = useMemo(() => templates.find((t) => t.id === templateId), [templates, templateId]);
    const canEditTemplate = !!currentTmpl && !currentTmpl.is_system;

    return (
        <>
        <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="email-composer">
                <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
                {loading ? (
                    <div className="py-10 text-center text-sm text-[#5E636E]">
                        <Loader2 className="inline h-4 w-4 animate-spin mr-2" /> Loading template…
                    </div>
                ) : (
                    <div className="space-y-3">
                        {showTemplatePicker && templates.length > 0 && (
                            <div className="rounded-lg border border-[#E4E4E7] bg-[#FAFAF7] p-3">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <Label className="text-[11px] text-[#5E636E] uppercase tracking-[0.14em]">Template</Label>
                                    <select
                                        value={templateId}
                                        onChange={(e) => switchTemplate(e.target.value)}
                                        className="rounded border border-[#E4E4E7] bg-white px-2 h-8 text-sm"
                                        data-testid="ec-template-picker"
                                    >
                                        {templates.map((t) => (
                                            <option key={t.id} value={t.id}>
                                                {t.name}
                                                {t.is_system ? " · system" : t.is_default ? " · default" : ""}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="ml-auto flex gap-1.5">
                                        <button type="button" onClick={() => setCloneOpen(true)} className="inline-flex items-center gap-1 rounded-md border border-[#E4E4E7] bg-white px-2.5 h-8 text-xs hover:bg-zinc-50" data-testid="ec-clone-btn" title="Clone this template as a new editable copy">
                                            <Copy className="h-3 w-3" /> Clone
                                        </button>
                                        <button type="button" onClick={saveEditsToTemplate} disabled={!canEditTemplate} className="inline-flex items-center gap-1 rounded-md border border-[#E4E4E7] bg-white px-2.5 h-8 text-xs disabled:opacity-40 hover:bg-zinc-50" data-testid="ec-save-template" title={canEditTemplate ? "Save current subject/HTML back to this template" : "System templates can't be edited — Clone first"}>
                                            Save to template
                                        </button>
                                        <button type="button" onClick={markDefault} disabled={!canEditTemplate} className="inline-flex items-center gap-1 rounded-md border border-[#E4E4E7] bg-white px-2.5 h-8 text-xs disabled:opacity-40 hover:bg-zinc-50" data-testid="ec-mark-default" title="Mark this template as the default for this kind">
                                            {currentTmpl?.is_default ? <Star className="h-3 w-3 fill-amber-500 text-amber-500" /> : <StarOff className="h-3 w-3" />} Default
                                        </button>
                                    </div>
                                </div>
                                {currentTmpl?.is_system && (
                                    <p className="mt-2 text-[11px] text-[#5E636E]">
                                        System templates are read-only. Clone to make your own editable version, then set it as default.
                                    </p>
                                )}
                            </div>
                        )}

                        <div>
                            <Label className="text-[11px] text-[#5E636E]">To</Label>
                            <Input
                                value={form.to}
                                onChange={(e) => setForm((f) => ({ ...f, to: e.target.value }))}
                                disabled={!toEditable}
                                className={`mt-1 ${toEditable ? "" : "bg-[#FAFAF7]"}`}
                                data-testid="ec-to"
                            />
                        </div>
                        <div>
                            <Label className="text-[11px] text-[#5E636E]">Subject</Label>
                            <Input
                                value={form.subject}
                                onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                                className="mt-1"
                                data-testid="ec-subject"
                            />
                        </div>
                        <div>
                            <div className="flex items-center justify-between">
                                <Label className="text-[11px] text-[#5E636E]">Email body</Label>
                                <div className="flex text-[11px] rounded-full border border-[#E4E4E7] overflow-hidden">
                                    <button
                                        type="button"
                                        onClick={() => setShowPreview(true)}
                                        className={`px-2.5 py-1 ${showPreview ? "bg-[#0F1115] text-white" : "bg-white text-[#5E636E]"}`}
                                        data-testid="ec-view-preview"
                                    >Preview</button>
                                    <button
                                        type="button"
                                        onClick={() => setShowPreview(false)}
                                        className={`px-2.5 py-1 ${!showPreview ? "bg-[#0F1115] text-white" : "bg-white text-[#5E636E]"}`}
                                        data-testid="ec-view-html"
                                    >Edit HTML</button>
                                </div>
                            </div>
                            {showPreview ? (
                                <div
                                    className="mt-1 border border-[#E4E4E7] rounded-md p-4 bg-white max-h-[420px] overflow-y-auto"
                                    dangerouslySetInnerHTML={{ __html: form.html }}
                                    data-testid="ec-preview"
                                />
                            ) : (
                                <Textarea
                                    rows={16}
                                    value={form.html}
                                    onChange={(e) => setForm((f) => ({ ...f, html: e.target.value }))}
                                    className="mt-1 font-mono text-[12px]"
                                    data-testid="ec-html"
                                />
                            )}
                        </div>

                        {/* Attachments */}
                        <div>
                            <div className="flex items-center justify-between">
                                <Label className="text-[11px] text-[#5E636E]">Attachments</Label>
                                <label className="inline-flex items-center gap-1 cursor-pointer text-xs text-[#0F1115] hover:underline">
                                    <Paperclip className="h-3 w-3" /> Add file
                                    <input
                                        ref={fileRef}
                                        type="file"
                                        multiple
                                        className="hidden"
                                        onChange={(e) => handleAttach(e.target.files)}
                                        data-testid="ec-attach-input"
                                    />
                                </label>
                            </div>
                            {attachments.length === 0 ? (
                                <div className="mt-1 text-[11px] text-[#5E636E]">No files attached. 10 MB per file · 20 MB total.</div>
                            ) : (
                                <ul className="mt-1 space-y-1 text-[12px]" data-testid="ec-attach-list">
                                    {attachments.map((a, i) => (
                                        <li key={i} className="flex items-center gap-2 rounded border border-[#E4E4E7] bg-[#FAFAF7] px-2 py-1">
                                            <Paperclip className="h-3 w-3 text-[#5E636E]" />
                                            <span className="truncate flex-1">{a.name}</span>
                                            <span className="text-[10px] text-[#5E636E]">{(a.size / 1024).toFixed(1)} KB</span>
                                            <button type="button" onClick={() => removeAttachment(i)} className="text-[#5E636E] hover:text-red-600" data-testid={`ec-attach-remove-${i}`}>
                                                <XIcon className="h-3 w-3" />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        {extraFooter}
                    </div>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={sending} data-testid="ec-cancel">Cancel</Button>
                    <Button
                        onClick={send}
                        disabled={sending || loading || !form.subject.trim() || !form.html.trim() || !form.to.trim()}
                        className="bg-[#171717] hover:bg-[#2E2E2E] text-white gap-2"
                        data-testid="ec-send"
                    >
                        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                        {sending ? "Sending…" : "Send email"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>

        <CloneTemplateDialog
            open={cloneOpen}
            onClose={() => setCloneOpen(false)}
            source={currentTmpl}
            currentSubject={form.subject}
            currentHtml={form.html}
            onCloned={(newTmpl) => {
                setTemplates((rows) => [...rows, newTmpl]);
                setTemplateId(newTmpl.id);
                setCloneOpen(false);
                // If the user has already edited on-screen, immediately persist those edits to the clone.
                if (currentTmpl && (form.subject !== currentTmpl.subject_template || form.html !== currentTmpl.html_template)) {
                    api.patch(`/email-templates/${newTmpl.id}`, {
                        subject_template: form.subject,
                        html_template: form.html,
                    }).then(({ data }) => setTemplates((rows) => rows.map((r) => (r.id === data.id ? data : r))))
                      .catch(() => {});
                }
            }}
        />
        </>
    );
}


function CloneTemplateDialog({ open, onClose, source, onCloned, currentSubject, currentHtml }) {
    const [name, setName] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open && source) {
            setName(`${source.name} (copy)`);
        }
    }, [open, source]);

    async function submit() {
        if (!source || !name.trim()) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/email-templates/${source.id}/clone`, { name: name.trim() });
            onCloned?.(data);
            toast.success(`Cloned to '${data.name}'`);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    const hasEdits = source && ((currentSubject && currentSubject !== source.subject_template) || (currentHtml && currentHtml !== source.html_template));

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent className="max-w-md" data-testid="ec-clone-dialog">
                <DialogHeader><DialogTitle>Clone template</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-[11px] text-[#5E636E]">Name your copy</Label>
                        <Input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="e.g. Annual — Prosaic (Michael)"
                            className="mt-1"
                            data-testid="ec-clone-name"
                            autoFocus
                        />
                    </div>
                    {hasEdits && (
                        <p className="text-[12px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md px-2.5 py-2">
                            Your on-screen edits (subject/body) will be saved into this new copy automatically.
                        </p>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button
                        onClick={submit}
                        disabled={busy || !name.trim()}
                        className="bg-[#171717] hover:bg-[#2E2E2E] text-white"
                        data-testid="ec-clone-submit"
                    >
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Clone"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
